/**
 * TopicMonitorUI
 *
 * Manage topic watches (create / delete) and view latest matching documents.
 * Maps to the `topic-monitor` platform tool (engine = 'internal').
 *
 * API:
 *   GET    /api/topic-watches               list user's watches
 *   POST   /api/topic-watches               { label, query }
 *   DELETE /api/topic-watches/:id
 *   GET    /api/topic-watches/:id/results   new chunk matches since last check
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { apiFetch } from '@/lib/apiFetch';
import {
  Plus,
  Trash2,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Bell,
  BellOff,
  FileText,
  Loader2,
} from 'lucide-react';

interface TopicWatch {
  id: string;
  label: string;
  query: string;
  last_checked_at?: string;
  created_at: string;
}

interface WatchResult {
  id: string;
  artifact_id?: string;
  text: string;
  created_at: string;
}

export const TopicMonitorUI: React.FC = () => {
  const [watches, setWatches] = useState<TopicWatch[]>([]);
  const [loadingWatches, setLoadingWatches] = useState(true);
  const [createLabel, setCreateLabel] = useState('');
  const [createQuery, setCreateQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, WatchResult[]>>({});
  const [loadingResults, setLoadingResults] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadWatches = useCallback(async () => {
    setLoadingWatches(true);
    try {
      const res = await apiFetch('/api/topic-watches');
      const data = await res.json();
      setWatches(data?.data ?? []);
    } catch {
      setWatches([]);
    } finally {
      setLoadingWatches(false);
    }
  }, []);

  useEffect(() => {
    void loadWatches();
  }, [loadWatches]);

  const handleCreate = async () => {
    if (!createLabel.trim() || !createQuery.trim()) return;
    setCreating(true);
    try {
      const res = await apiFetch('/api/topic-watches', {
        method: 'POST',
        body: JSON.stringify({
          label: createLabel.trim(),
          query: createQuery.trim(),
        }),
      });
      if (!res.ok) throw new Error('Failed to create watch');
      setCreateLabel('');
      setCreateQuery('');
      setShowCreate(false);
      await loadWatches();
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    await apiFetch(`/api/topic-watches/${id}`, { method: 'DELETE' });
    setWatches((prev) => prev.filter((w) => w.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const handleCheckResults = async (id: string) => {
    setLoadingResults(id);
    setErrors((prev) => ({ ...prev, [id]: '' }));
    try {
      const res = await apiFetch(`/api/topic-watches/${id}/results`);
      const data = await res.json();
      setResults((prev) => ({ ...prev, [id]: data?.data ?? [] }));
      // Update last_checked_at locally
      setWatches((prev) =>
        prev.map((w) =>
          w.id === id ? { ...w, last_checked_at: new Date().toISOString() } : w
        )
      );
    } catch (e) {
      setErrors((prev) => ({ ...prev, [id]: (e as Error).message }));
    } finally {
      setLoadingResults(null);
    }
  };

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      if (!results[id]) {
        await handleCheckResults(id);
      }
    }
  };

  const formatDate = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
      : '—';

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Topic Watches</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Get notified when new county documents match your search topics.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" />
          New Watch
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Watch label
              </label>
              <Input
                value={createLabel}
                onChange={(e) => setCreateLabel(e.target.value)}
                placeholder="e.g. Zoning Decisions"
                className="bg-white"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Search query
              </label>
              <Input
                value={createQuery}
                onChange={(e) => setCreateQuery(e.target.value)}
                placeholder="e.g. zoning variance rezoning"
                className="bg-white"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleCreate();
                }}
              />
              <p className="text-xs text-gray-400 mt-1">
                Supports full-text search terms (websearch syntax).
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={
                  !createLabel.trim() || !createQuery.trim() || creating
                }
                onClick={handleCreate}
              >
                {creating && (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                )}
                Save Watch
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Watch list */}
      {loadingWatches ? (
        <div className="flex items-center justify-center py-10 text-gray-400 gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading watches…</span>
        </div>
      ) : watches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <BellOff className="h-10 w-10 text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-600">No watches yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Create a watch to start monitoring for new county documents.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {watches.map((watch) => (
            <Card key={watch.id} className="overflow-hidden">
              {/* Watch row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <Bell className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {watch.label}
                    </span>
                    <Badge variant="outline" className="text-xs font-normal">
                      {watch.query}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Last checked: {formatDate(watch.last_checked_at)}
                  </p>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Refresh results"
                    onClick={() => void handleCheckResults(watch.id)}
                    disabled={loadingResults === watch.id}
                  >
                    {loadingResults === watch.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:text-red-700"
                    title="Delete watch"
                    onClick={() => void handleDelete(watch.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => void toggleExpand(watch.id)}
                  >
                    {expandedId === watch.id ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Results panel */}
              {expandedId === watch.id && (
                <div className="border-t bg-gray-50 px-4 py-3">
                  {errors[watch.id] && (
                    <p className="text-xs text-red-600 mb-2">
                      {errors[watch.id]}
                    </p>
                  )}
                  {loadingResults === watch.id ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking for new matches…
                    </div>
                  ) : (results[watch.id] ?? []).length === 0 ? (
                    <p className="text-xs text-gray-400 py-2">
                      No new matching documents since last check.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {results[watch.id].length} new match
                        {results[watch.id].length !== 1 ? 'es' : ''}
                      </p>
                      {results[watch.id].map((r) => (
                        <div
                          key={r.id}
                          className="flex items-start gap-2 text-xs text-gray-700 bg-white rounded-lg border px-3 py-2"
                        >
                          <FileText className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <p className="line-clamp-3 leading-relaxed">
                            {r.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
